"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import Link from "next/link";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Countdown } from "@/features/home-v2/live";
import { Creator, TILE_PLACEHOLDER, chainName } from "@/features/home-v2/tiles";
import SnapRailNav from "@/features/home-v2/SnapRailNav";
import { useSnapRail } from "@/features/home-v2/useSnapRail";
import { sanitizeUrl } from "@/lib/helpers";
import { parseDropDateTime } from "../permissions";
import type { Drop } from "../types";
import { dropFigures } from "../dropFigures";
import { pricePartsOf } from "./itemFacts";
import "./drop-spotlight.css";
import PercentFigure from "@/components/shared/PercentFigure";

/** Five slides is where a hand-picked shelf stops being scannable. */
const MAX = 5;
function Slide(t0) {
  const $ = _c(73);
  const {
    drop,
    lang,
    first
  } = t0;
  const isFa = lang === "fa";
  let t1;
  if ($[0] !== isFa) {
    t1 = (fa, en) => isFa ? fa : en;
    $[0] = isFa;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  let t2;
  if ($[2] !== drop || $[3] !== first || $[4] !== isFa || $[5] !== lang || $[6] !== t) {
    const nf = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US");
    const Go = isFa ? ArrowLeft : ArrowRight;
    const fig = dropFigures(drop);
    const soldOut = drop.status === "minting" && fig.soldOut;
    const empty = drop.status === "minting" && !soldOut && fig.empty;
    const live = drop.status === "minting" && !soldOut && !empty;
    const href = `/${lang}/drops/${drop.slug || drop.id}`;
    const items = drop.items ?? [];
    const price = pricePartsOf(fig.range, fig.lowest, nf);
    const supply = fig.supply;
    const royalty = drop.meta_data?.owner_has_model?.royalty_percent ?? null;
    const clockAt = soldOut || empty ? null : parseDropDateTime(live ? drop.endAt : drop.startAt);
    const works = items.filter(_temp).slice(0, 3);
    const t3 = drop.creator?.name;
    let t4;
    if ($[8] !== drop.creator?.username) {
      t4 = drop.creator?.username?.replace(/^@/, "");
      $[8] = drop.creator?.username;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    const t5 = drop.creator?.avatar;
    const t6 = drop.creator?.verified ? 1 : 0;
    let t7;
    if ($[10] !== t3 || $[11] !== t4 || $[12] !== t5 || $[13] !== t6) {
      t7 = {
        name: t3,
        username: t4,
        avatar: t5,
        blue_check: t6
      };
      $[10] = t3;
      $[11] = t4;
      $[12] = t5;
      $[13] = t6;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    const owner = t7;
    let t8;
    if ($[15] !== t) {
      t8 = t("\u0627\u0633\u0644\u0627\u06CC\u062F", "slide");
      $[15] = t;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    const t9 = drop.title;
    const t10 = drop.bannerImage || drop.coverImage || TILE_PLACEHOLDER;
    const t11 = first ? "high" : undefined;
    const t12 = first ? "eager" : "lazy";
    let t13;
    if ($[17] !== first || $[18] !== t10 || $[19] !== t11 || $[20] !== t12) {
      t13 = <Image src={t10} alt="" fill={true} preload={first} fetchPriority={t11} loading={t12} sizes="(max-width: 1023px) 90vw, 700px" className="dr-slide-art" />;
      $[17] = first;
      $[18] = t10;
      $[19] = t11;
      $[20] = t12;
      $[21] = t13;
    } else {
      t13 = $[21];
    }
    let t14;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <span className="dr-slide-shade" aria-hidden={true} />;
      $[22] = t14;
    } else {
      t14 = $[22];
    }
    let t15;
    if ($[23] !== drop.title || $[24] !== href) {
      t15 = <Link href={href} className="dr-slide-link v2-focus" aria-label={drop.title} />;
      $[23] = drop.title;
      $[24] = href;
      $[25] = t15;
    } else {
      t15 = $[25];
    }
    const t16 = live ? "live" : soldOut ? "soldout" : empty ? "empty" : "soon";
    let t17;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <span className="v2-live-dot" aria-hidden={true} />;
      $[26] = t17;
    } else {
      t17 = $[26];
    }
    const t18 = live ? t("\u062F\u0631 \u062D\u0627\u0644 \u0645\u06CC\u0646\u062A", "Minting now") : soldOut ? t("\u0647\u0645\u0647 \u0645\u06CC\u0646\u062A \u0634\u062F", "Sold out") : empty ? t("\u0628\u062F\u0648\u0646 \u0622\u06CC\u062A\u0645", "No items yet") : t("\u0628\u0647\u200C\u0632\u0648\u062F\u06CC", "Upcoming");
    let t19;
    if ($[27] !== t16 || $[28] !== t18) {
      t19 = <span className="dr-slide-phase" data-phase={t16}>{t17}{t18}</span>;
      $[27] = t16;
      $[28] = t18;
      $[29] = t19;
    } else {
      t19 = $[29];
    }
    let t20;
    if ($[30] !== drop.title) {
      t20 = <h2>{drop.title}</h2>;
      $[30] = drop.title;
      $[31] = t20;
    } else {
      t20 = $[31];
    }
    let t21;
    if ($[32] !== t) {
      t21 = t("\u0646\u0627\u0634\u0646\u0627\u0633", "Unknown");
      $[32] = t;
      $[33] = t21;
    } else {
      t21 = $[33];
    }
    let t22;
    if ($[34] !== lang || $[35] !== owner || $[36] !== t21) {
      t22 = <Creator owner={owner} lang={lang} fallback={t21} />;
      $[34] = lang;
      $[35] = owner;
      $[36] = t21;
      $[37] = t22;
    } else {
      t22 = $[37];
    }
    let t23;
    if ($[38] !== drop.chain || $[39] !== isFa) {
      t23 = drop.chain ? <span className="v2-chip v2-chip--chain" data-chain={drop.chain.toLowerCase()}>{chainName(drop.chain, isFa)}</span> : null;
      $[38] = drop.chain;
      $[39] = isFa;
      $[40] = t23;
    } else {
      t23 = $[40];
    }
    let t24;
    if ($[41] !== t22 || $[42] !== t23) {
      t24 = <div className="dr-slide-meta">{t22}{t23}</div>;
      $[41] = t22;
      $[42] = t23;
      $[43] = t24;
    } else {
      t24 = $[43];
    }
    let t25;
    if ($[44] !== t) {
      t25 = t("\u0642\u06CC\u0645\u062A \u0627\u0632", "From");
      $[44] = t;
      $[45] = t25;
    } else {
      t25 = $[45];
    }
    let t26;
    if ($[46] !== t25) {
      t26 = <dt>{t25}</dt>;
      $[46] = t25;
      $[47] = t26;
    } else {
      t26 = $[47];
    }
    const t27 = <div>{t26}<dd>{price ? price.map(_temp2) : "\u2014"}</dd></div>;
    let t28;
    if ($[48] !== t) {
      t28 = t("\u0622\u06CC\u062A\u0645\u200C\u0647\u0627", "Items");
      $[48] = t;
      $[49] = t28;
    } else {
      t28 = $[49];
    }
    let t29;
    if ($[50] !== t28) {
      t29 = <dt>{t28}</dt>;
      $[50] = t28;
      $[51] = t29;
    } else {
      t29 = $[51];
    }
    const t30 = <div>{t29}<dd>{nf.format(fig.itemsCount)}</dd></div>;
    const t31 = supply !== null && supply !== fig.itemsCount ? <div><dt>{t("\u0645\u0648\u062C\u0648\u062F\u06CC \u06A9\u0644", "Total supply")}</dt><dd>{nf.format(supply)}</dd></div> : null;
    const t32 = royalty !== null ? <div><dt>{t("\u0631\u0648\u06CC\u0627\u0644\u062A\u06CC", "Royalty")}</dt><dd><PercentFigure figure={nf.format(royalty)} isFa={isFa} /></dd></div> : null;
    let t33;
    if ($[52] !== t27 || $[53] !== t30 || $[54] !== t31 || $[55] !== t32) {
      t33 = <dl className="dr-slide-facts">{t27}{t30}{t31}{t32}</dl>;
      $[52] = t27;
      $[53] = t30;
      $[54] = t31;
      $[55] = t32;
      $[56] = t33;
    } else {
      t33 = $[56];
    }
    let t34;
    if ($[57] !== t) {
      t34 = t("\u0645\u0634\u0627\u0647\u062F\u0647 \u062F\u0631\u0627\u067E", "View drop");
      $[57] = t;
      $[58] = t34;
    } else {
      t34 = $[58];
    }
    let t35;
    if ($[59] !== Go) {
      t35 = <Go aria-hidden={true} />;
      $[59] = Go;
      $[60] = t35;
    } else {
      t35 = $[60];
    }
    let t36;
    if ($[61] !== t34 || $[62] !== t35) {
      t36 = <span className="v2-btn dr-slide-cta" aria-hidden={true}>{t34}{t35}</span>;
      $[61] = t34;
      $[62] = t35;
      $[63] = t36;
    } else {
      t36 = $[63];
    }
    const t37 = clockAt !== null ? <span className="dr-slide-clock"><span>{live ? t("\u067E\u0627\u06CC\u0627\u0646 \u0645\u06CC\u0646\u062A", "Minting ends") : t("\u0634\u0631\u0648\u0639 \u0645\u06CC\u0646\u062A", "Minting starts")}</span><Countdown iso={new Date(clockAt).toISOString()} isFa={isFa} fallback={"\u2014"} /></span> : null;
    let t38;
    if ($[64] !== t36 || $[65] !== t37) {
      t38 = <div className="dr-slide-foot">{t36}{t37}</div>;
      $[64] = t36;
      $[65] = t37;
      $[66] = t38;
    } else {
      t38 = $[66];
    }
    let t39;
    if ($[67] !== t19 || $[68] !== t20 || $[69] !== t24 || $[70] !== t33 || $[71] !== t38) {
      t39 = <div className="dr-slide-copy">{t19}{t20}{t24}{t33}{t38}</div>;
      $[67] = t19;
      $[68] = t20;
      $[69] = t24;
      $[70] = t33;
      $[71] = t38;
      $[72] = t39;
    } else {
      t39 = $[72];
    }
    t2 = <article className="dr-slide" aria-roledescription={t8} aria-label={t9}>{t13}{t14}{t15}{t39}{works.length > 0 ? <ul className="dr-slide-works" aria-label={t("\u0622\u06CC\u062A\u0645\u200C\u0647\u0627\u06CC \u0627\u06CC\u0646 \u062F\u0631\u0627\u067E", "Items in this drop")}>{works.map(w => <li key={w.id}><Link href={href} className="v2-focus" aria-label={isFa && w.meta_data?.fa_name || w.name} tabIndex={-1}><Image src={sanitizeUrl(w.meta_data.nft_cover ?? undefined) as string} alt="" fill={true} loading="lazy" sizes="88px" /></Link></li>)}</ul> : null}</article>;
    $[2] = drop;
    $[3] = first;
    $[4] = isFa;
    $[5] = lang;
    $[6] = t;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

/**
 * THE LAUNCH PAD — the live and upcoming drops as a swipeable shelf: two side
 * by side on a wide screen, one with the next peeking on a phone. A native
 * scroll-snap track (no slider library, no JS on the swipe itself), so touch,
 * trackpad and keys all move it as the platform does; arrows and dots drive
 * the same scroll (home-v2/useSnapRail). Nothing advances on its own.
 */
function _temp2(x, i_0) {
  return <span key={x.symbol} className="v2-drop-part">{i_0 > 0 ? <i aria-hidden={true}>·</i> : null}<bdi>{x.text}</bdi><small>{x.symbol}</small></span>;
}
function _temp(i) {
  return sanitizeUrl(i.meta_data?.nft_cover ?? undefined);
}
export default function DropSpotlight({
  drops,
  lang
}: {
  drops: Drop[];
  lang: "fa" | "en";
}) {
  const isFa = lang === "fa";
  const t = (fa: string, en: string) => isFa ? fa : en;
  const items = drops.slice(0, MAX);
  const rail = useSnapRail(items.length);
  if (items.length === 0) return null;
  return <section className="dr-spot" aria-roledescription={t("اسلایدر", "carousel")} aria-label={t("دراپ‌های لایو و به‌زودی", "Live and upcoming drops")}>
      {/* A single drop takes the whole row: there is no second one to sit beside it. */}
      <div className="dr-track" ref={rail.track} data-single={items.length === 1 || undefined}>
        {items.map((d, i) => <Slide key={d.id} drop={d} lang={lang} first={i === 0} />)}
      </div>
      <SnapRailNav stops={rail.stops} index={rail.index} go={rail.go} labels={items.map(d_0 => d_0.title)} isFa={isFa} groupLabel={t("انتخاب دراپ", "Choose a drop")} />
    </section>;
}